import React from 'react';
import { toast } from 'sonner';
import { Link2, Download, CalendarCheck, Mail, UserRound, Award } from 'lucide-react';
import { Button } from '../../ui/button';
import { BrandMark } from '../BrandMark';
import { ScoreRing, LevelBadge, Metric } from './ReportBits';
import SkillGapPanel from './SkillGapPanel';
import AiInsights from './AiInsights';
import ItemBreakdown from './ItemBreakdown';
import IntegrityPanel from './IntegrityPanel';
import { assessmentPdfUrl, assessmentReportUrl } from '../../../services/assessmentApi';

const copy = async (text) => {
  try { await navigator.clipboard.writeText(text); toast.success('Share link copied — no password needed'); }
  catch { toast.info(text); }
};

export const ReportView = ({ report, embedded = false }) => {
  const res = report.results;
  const used = res.integrity.time_used_seconds || 0;
  const vis = report.visibility || {};
  const internal = !report.shared;
  return (
    <div className="space-y-6 text-slate-900" data-testid="assessment-report">
      <div className="flex flex-wrap items-center justify-between gap-4">
        {!embedded ? <BrandMark /> : <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Candidate report</span>}
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2 bg-white" onClick={() => copy(assessmentReportUrl(report.share_token))} data-testid="copy-report-share-link-btn">
            <Link2 className="h-4 w-4" /> Copy share link
          </Button>
          <Button asChild className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="download-pdf-report-btn">
            <a href={assessmentPdfUrl(report.share_token)}><Download className="h-4 w-4" /> Download PDF</a>
          </Button>
        </div>
      </div>

      <section className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-indigo-100/60 blur-3xl pointer-events-none" />
        <div className="relative flex flex-col lg:flex-row gap-8 lg:items-center">
          <ScoreRing score={res.overall_score} />
          <div className="flex-1 space-y-3 min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">{report.test_title}</p>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="report-candidate-name">
              {report.candidate.name}
            </h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
              <span className="flex items-center gap-1.5" data-testid="report-masked-candidate-email"><Mail className="h-3.5 w-3.5" /> {report.candidate.email}</span>
              <span className="flex items-center gap-1.5"><CalendarCheck className="h-3.5 w-3.5" /> {report.completed_display}</span>
              <span className="flex items-center gap-1.5"><UserRound className="h-3.5 w-3.5" /> Email masked for privacy</span>
            </div>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <LevelBadge level={res.level} />
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 bg-slate-100 px-3 py-1 rounded-full" data-testid="report-recommendation">
                <Award className="h-3.5 w-3.5" /> {res.recommendation}
              </span>
              {report.shared_recommendation && (
                <span className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full" data-testid="report-shared-recommendation">
                  Shared report shows: {report.shared_recommendation}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="relative grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          <Metric label="Hands-on (live sheet)" value={`${res.hands_on_score}%`} sub={`${res.stats.hands_on_total} tasks`} testid="report-handson-score" />
          <Metric label="Scenario questions" value={`${res.scenario_score}%`} sub={`${res.stats.scenario_total} questions`} testid="report-scenario-score" />
          <Metric label="Answered" value={`${res.stats.answered}/${res.stats.total}`} sub={`${res.stats.correct} fully correct`} testid="report-answered" />
          <Metric label="Time used" value={`${Math.floor(used / 60)}m ${String(used % 60).padStart(2, '0')}s`} sub="of 20 minutes" testid="report-time-used" />
        </div>
      </section>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 items-start">
        <SkillGapPanel skills={res.skills} />
        <div className="space-y-6">
          {res.integrity.risk && <IntegrityPanel integrity={res.integrity} submitMode={report.submit_mode} hidden={internal && vis.integrity === false} />}
          <section className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="text-sm font-bold text-slate-900 mb-2">Skills not claimed (not tested)</h3>
            <div className="flex flex-wrap gap-1.5">
              {report.skills_not_selected.map((s) => <span key={s} className="text-[11px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{s}</span>)}
            </div>
          </section>
        </div>
      </div>
      <AiInsights ai={report.ai_feedback} status={report.ai_status} visibility={vis} internal={internal} lpStatus={report.lp_status} />
      <ItemBreakdown items={res.items} />
      <p className="text-center text-[11px] text-slate-400 pt-2">HireFast · TekWissen Evolution in AI · Confidential candidate assessment</p>
    </div>
  );
};

export default ReportView;
